import React, { memo, useRef, useEffect, useState } from 'react';
import { useSprings, animated } from '@react-spring/web';
import Typography from './Typography';

const BlurText = memo(({
  text = '',
  delay = 200,
  className = '',
  animateBy = 'letters', // 'words' or 'letters'
  direction = 'top', // 'top' or 'bottom'
  threshold = 0.1,
  rootMargin = '0px',
  animationFrom,
  animationTo,
  easing = 'easeOutCubic',
  typographyVariant = "summary",
  typographyType = "editorial",
  onAnimationComplete,
}) => {
  const elements = animateBy === 'words' ? text.split(' ') : text.split('');
  const inViewRef = useRef(false); // ✅ Prevents multiple re-renders
  const [inView, setInView] = useState(false);
  const ref = useRef();

  const defaultFrom = { filter: 'blur(10px)', opacity: 0, transform: 'translate3d(0,-50px,0)' };
  const defaultTo = [
    { filter: 'blur(5px)', opacity: 0.5, transform: 'translate3d(0,5px,0)' },
    { filter: 'blur(0px)', opacity: 1, transform: 'translate3d(0,0,0)' },
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !inViewRef.current) {
          setInView(true);
          inViewRef.current = true; // ✅ Prevent multiple triggers
          observer.unobserve(ref.current);
        }
      },
      { threshold, rootMargin }
    );

    if (ref.current) observer.observe(ref.current);

    return () => observer.disconnect();
  }, [threshold, rootMargin]);

  const springs = useSprings(
    elements.length,
    inView
      ? elements.map((_, i) => ({
          from: animationFrom || defaultFrom,
          to: async (next) => {
            for (const step of (animationTo || defaultTo)) await next(step);
            if (i === elements.length - 1 && onAnimationComplete) onAnimationComplete();
          },
          delay: i * delay,
          config: { easing },
        }))
      : elements.map(() => ({ from: defaultFrom }))
  );

  return (
    <p ref={ref} className={`blur-text ${className}`}>
      <Typography variant={typographyVariant} type={typographyType} className={`blur-text ${className}`}>
        {springs.map((props, index) => (
          <animated.span
            key={index}
            style={{ ...props, display: 'inline-block', willChange: 'transform, filter, opacity' }}
          >
            {elements[index] === ' ' ? '\u00A0' : elements[index]}
            {animateBy === 'words' && index < elements.length - 1 && '\u00A0'}
          </animated.span>
        ))}
      </Typography>
    </p>
  );
});

export default BlurText;
